import React from "react";
import { Trash2 } from "lucide-react";
import { CreateTOProductColumnHeader } from "src/enums";
import { TableColumn, CreateTOProductInputField } from "src/types";
import { TextInputBox } from "src/components";
import { Table } from "src/components";

interface CreateTOProductInputTableProps {
  products: CreateTOProductInputField[];
  setProducts: React.Dispatch<React.SetStateAction<CreateTOProductInputField[]>>;
}

const CreateTOProductInputTable: React.FC<CreateTOProductInputTableProps> = ({ products, setProducts }) => {
  const handleProductChange = <T,>(fieldId: string, fieldName: keyof CreateTOProductInputField, value: T) => {
    setProducts(
      products.map((product) => (product.fieldId === fieldId ? { ...product, [fieldName]: value } : product))
    );
  };

  const createTOProductTableColumns: TableColumn<CreateTOProductColumnHeader, CreateTOProductInputField>[] = [
    {
      header: CreateTOProductColumnHeader.PRODUCT_ID,
      column: (row) => (
        <div className="min-w-[200px]">
          <TextInputBox
            id={`product-id-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.productId ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "productId", value)}
            placeholder="Enter 6-15 digit ID"
            maxLength={15}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "220px" }
    },
    {
      header: CreateTOProductColumnHeader.EU_QTY,
      column: (row) => (
        <div className="w-28">
          <TextInputBox
            id={`eu-qty-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.euQuantity ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "euQuantity", value)}
            placeholder="EU"
            maxLength={10}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: CreateTOProductColumnHeader.EXTRA_INFO,
      column: (row) => (
        <div className="min-w-[250px]">
          <TextInputBox
            id={`extra-info-${row.fieldId}`}
            value={(products.find((product) => product.fieldId === row.fieldId)?.extraInfo ?? "").toString()}
            onChange={(value) => handleProductChange(row.fieldId, "extraInfo", value)}
            placeholder="Optional notes..."
            maxLength={200}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "260px" }
    },
    {
      header: CreateTOProductColumnHeader.ACTIONS,
      column: (row) => (
        <button
          onClick={() => setProducts(products.filter((product) => product.fieldId !== row.fieldId))}
          disabled={products.length === 1}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete product"
          type="button"
        >
          <Trash2 className="h-4 w-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return (
    <Table<CreateTOProductColumnHeader, CreateTOProductInputField>
      data={products}
      columns={createTOProductTableColumns}
    />
  );
};

export { CreateTOProductInputTable };
